import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { orderAPI, paymentMethodsAPI, uploadAPI, paymentTransactionsAPI } from '../services/api';
import api from '../services/api';
import { CreditCard, Building2, Truck, ArrowLeft, Upload, CheckCircle, FileText, Check, ShoppingCart } from 'lucide-react';
import ProgressBar from '../components/Checkout/ProgressBar';
import CashfreePayment from '../components/Payment/CashfreePayment';
import { useNotificationHook } from '../hooks/useNotificationHook';


const Payment = () => {
  const { notify } = useNotificationHook();
  const navigate = useNavigate();
  const { items, getTotal, getSubtotal, getDiscount, clearCart, appliedCoupon } = useCart();
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const [selectedPayment, setSelectedPayment] = useState('');
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [showOfflineDetails, setShowOfflineDetails] = useState(false);
  const [showCashfreePayment, setShowCashfreePayment] = useState(false);
  const [showCodDetails, setShowCodDetails] = useState(false);

  const [offlineProof, setOfflineProof] = useState(null);
  const [saveDetails, setSaveDetails] = useState(true);
  const [loading, setLoading] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [paymentOptionsConfig, setPaymentOptionsConfig] = useState(null);

  // COD form state
  const [codFormData, setCodFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    whatsapp: user?.whatsapp || ''
  });

  // Offline Payment form state
  const [offlineFormData, setOfflineFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    whatsapp: user?.whatsapp || ''
  });

  useEffect(() => {
    // Only redirect to cart if we're not coming from a successful payment
    const orderDetails = localStorage.getItem('orderDetails');
    if (items.length === 0 && !orderDetails) {
      navigate('/cart');
      return;
    }
    
    // Load checkout data
    const savedCheckoutData = localStorage.getItem('checkoutData');
    if (savedCheckoutData) {
      const parsedData = JSON.parse(savedCheckoutData);
      setCheckoutData(parsedData);

    }
    
    fetchPaymentConfig();
    fetchPaymentOptionsConfig();
  }, [items, navigate]);

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      setCodFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        whatsapp: user.whatsapp || ''
      }));
      
      setOfflineFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        whatsapp: user.whatsapp || ''
      }));
    }
  }, [user]);

  const fetchPaymentConfig = async () => {
    try {
      const response = await paymentMethodsAPI.getConfig();
      setPaymentConfig(response.data.data);
    } catch (error) {
      console.error('Error fetching payment config:', error);
    }
  };

  const fetchPaymentOptionsConfig = async () => {
    try {
      const response = await api.get('/payment-options/config');
      if (response?.data?.success) {
        const configData = {};
        Object.keys(response.data.data).forEach(key => {
          configData[key] = response.data.data[key].value;
        });
        setPaymentOptionsConfig(configData);
      }
    } catch (error) {
      console.error('Error fetching payment options config:', error);
    }
  };

  const paymentMethods = [
    {
      id: 'cashfree',
      name: 'Cashfree Payment',
      icon: CreditCard,
      description: 'Secure online payment gateway',
      color: 'text-orange-600',
      enabled: paymentConfig?.cashfree?.is_active || false
    },
    {
      id: 'offline',
      name: 'Offline Payment',
      icon: Building2,
      description: 'Bank transfer, UPI, or cash payment',
      color: 'text-green-600',
      enabled: paymentConfig?.offline?.is_active || false
    },
    {
      id: 'cod',
      name: 'Cash On Delivery',
      icon: Truck,
      description: 'Pay when you receive your order',
      color: 'text-blue-600',
      enabled: paymentConfig?.cod?.is_active || false
    }
  ];

  const handlePaymentSelection = (paymentId) => {
    setSelectedPayment(paymentId);
    setShowOfflineDetails(paymentId === 'offline');
    setShowCashfreePayment(paymentId === 'cashfree');
    setShowCodDetails(paymentId === 'cod');
  };

  const handleBackToCheckout = () => {
    navigate('/checkout');
  };

  // Calculate the correct amount based on selected payment option
  const getPaymentAmount = () => {
    if (!checkoutData?.selectedPaymentOption || !paymentOptionsConfig) {
      return getTotal();
    }

    const { selectedPaymentOption } = checkoutData;
    const total = getTotal();

    switch (selectedPaymentOption) {
      case 'advance':
        return (total * (paymentOptionsConfig.advance_payment_percentage || 50)) / 100;
      case 'emi':
        return (total * (paymentOptionsConfig.emi_advance_percentage || 50)) / 100;
      default:
        return total;
    }
  };

  // Get payment option display text
  const getPaymentOptionText = () => {
    if (!checkoutData?.selectedPaymentOption) return 'Full Payment';
    
    const { selectedPaymentOption } = checkoutData;
    switch (selectedPaymentOption) {
      case 'advance':
        return 'Split Payment';
      case 'emi':
        return 'EMI Payment';
      default:
        return 'Full Payment';
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setOfflineProof(file);
    }
  };

  const handleCodFormChange = (field, value) => {
    setCodFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOfflineFormChange = (field, value) => {
    setOfflineFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Check if cart contains EMI installment payments
  const hasEMIInstallmentPayments = () => {
    return items.some(item => item.type === 'payment' && item.paymentData?.type === 'emi_payment');
  };

  // Process EMI installment payment
  const processEMIInstallmentPayment = async (paymentData) => {
    try {
      console.log('🔄 Processing EMI installment payment:', paymentData);
      
      // Update the existing payment transaction
      const updateData = {
        payment_status: 'completed',
        installment_status: 'completed',
        payment_date: new Date().toISOString(),
        payment_method: 'Cash On Delivery', // or the actual payment method used
        notes: `EMI installment payment completed via ${paymentData.paymentMethod || 'Cash On Delivery'}`
      };

      const response = await paymentTransactionsAPI.update(paymentData.transaction_id, updateData);
      
      if (response?.data?.success) {
        console.log('✅ EMI installment payment updated successfully');
        return response.data;
      } else {
        throw new Error('Failed to update EMI installment payment');
      }
    } catch (error) {
      console.error('❌ Error processing EMI installment payment:', error);
      throw error;
    }
  };

  // Process Split Payment - Create new order for the split payment
  const processSplitPayment = async (paymentData) => {
    try {
      console.log('🔄 Processing Split Payment:', paymentData);
      
      // Calculate coupon discount amount for split payment
      const couponDiscountAmount = appliedCoupon ? (() => {
        const subtotal = paymentData.amount;
        
        if (appliedCoupon.discount_amount && appliedCoupon.discount_amount > 0) {
          return Math.min(appliedCoupon.discount_amount, subtotal);
        } else if (appliedCoupon.discount_percentage && appliedCoupon.discount_percentage > 0) {
          return (subtotal * appliedCoupon.discount_percentage) / 100;
        }
        
        return 0;
      })() : 0;

      // Create a new order for the split payment (this will be a regular order, not a split payment transaction)
      const orderData = {
        customer_name: user?.name || 'Guest User',
        customer_email: user?.email || 'guest@example.com',
        customer_phone: user?.phone || '',
        total_amount: paymentData.amount - couponDiscountAmount, // Apply coupon discount
        payment_method: paymentData.paymentMethod || 'Cash On Delivery',
        payment_id: null,
        billing_address: null,
        notes: `${paymentData.split_payment_text || 'Split Payment'} for Order #${paymentData.order_number} via ${paymentData.paymentMethod || 'Cash On Delivery'}`,
        payment_option: 'full', // This is a full payment order, not a split payment transaction
        payment_option_details: {
          selectedPaymentOption: 'full',
          split_payment_type: paymentData.split_payment_type,
          split_payment_text: paymentData.split_payment_text,
          parent_split_payment_id: paymentData.parent_split_payment_id,
          original_order_number: paymentData.order_number,
          original_total_amount: paymentData.total_amount,
          paid_amount: paymentData.paid_amount,
          remaining_amount: paymentData.remaining_amount,
          is_split_payment_order: true, // Flag to identify this as a split payment order
          // Include coupon information
          appliedCoupon: appliedCoupon ? {
            ...appliedCoupon,
            calculated_discount_amount: couponDiscountAmount
          } : null
        },
        // Include coupon information at order level
        applied_coupon: appliedCoupon ? JSON.stringify({
          ...appliedCoupon,
          calculated_discount_amount: couponDiscountAmount
        }) : null,
        original_total_amount: paymentData.total_amount,
        advance_amount: paymentData.amount - couponDiscountAmount, // Apply coupon discount
        remaining_amount: paymentData.remaining_amount - (paymentData.amount - couponDiscountAmount),
        items: [] // No items for split payments
      };

      console.log('📦 Split Payment order data:', orderData);

      // Create the order
      const orderResponse = await orderAPI.create(orderData);
      
      console.log('📦 Split Payment order response:', orderResponse);
      
      if (orderResponse?.data?.success) {
        console.log('✅ Split Payment order created successfully');
        
        // Update the original split payment transaction to mark this payment type as completed
        if (paymentData.parent_split_payment_id) {
          try {
            console.log('🔄 Updating original split payment transaction:', paymentData.parent_split_payment_id);
            
            // Get current payment option details from the original transaction
            const currentTransactionResponse = await paymentTransactionsAPI.getById(paymentData.parent_split_payment_id);
            let currentPaymentOptionDetails = {};
            
            if (currentTransactionResponse?.data?.success) {
              const currentTransaction = currentTransactionResponse.data.data;
              if (currentTransaction.payment_option_details) {
                try {
                  currentPaymentOptionDetails = typeof currentTransaction.payment_option_details === 'string' 
                    ? JSON.parse(currentTransaction.payment_option_details) 
                    : currentTransaction.payment_option_details;
                } catch (e) {
                  console.warn('Failed to parse current payment option details:', e);
                }
              }
            }
            
            // Update payment option details to mark this payment type as completed
            const updatedPaymentOptionDetails = {
              ...currentPaymentOptionDetails,
              last_payment_type: paymentData.split_payment_type,
              [`${paymentData.split_payment_type}_completed`]: true,
              [`${paymentData.split_payment_type}_completed_date`]: new Date().toISOString(),
              [`${paymentData.split_payment_type}_order_id`]: orderResponse.data.data.id,
              [`${paymentData.split_payment_type}_order_number`]: orderResponse.data.data.order_number,
              [`${paymentData.split_payment_type}_order_payment_status`]: orderResponse.data.data.payment_status || 'completed'
            };
            
            // Update the original split payment transaction
            const updateData = {
              payment_option_details: JSON.stringify(updatedPaymentOptionDetails),
              notes: `${paymentData.split_payment_text} payment completed. Order #${orderResponse.data.data.order_number} created.`
            };
            
            const updateResponse = await paymentTransactionsAPI.updateUser(paymentData.parent_split_payment_id, updateData);
            
            if (updateResponse?.data?.success) {
              console.log('✅ Original split payment transaction updated successfully');
            } else {
              console.warn('⚠️ Failed to update original split payment transaction:', updateResponse);
            }
          } catch (updateError) {
            console.error('❌ Error updating original split payment transaction:', updateError);
            // Don't throw error here as the order was created successfully
          }
        }
        
        // Save order details to localStorage for ThankYou page
        const orderDetails = {
          orderNumber: orderResponse.data.data.order_number,
          paymentMethod: paymentData.paymentMethod || 'Cash On Delivery',
          total: paymentData.amount - couponDiscountAmount, // Apply coupon discount
          date: new Date().toISOString(),
          paymentStatus: orderResponse.data.data.payment_status || 'completed',
          orderStatus: orderResponse.data.data.order_status || 'pending',
          orderId: orderResponse.data.data.order_id,
          isSplitPayment: true,
          splitPaymentType: paymentData.split_payment_type,
          splitPaymentText: paymentData.split_payment_text,
          notes: `${paymentData.split_payment_text || 'Split Payment'} for Order #${paymentData.order_number}`,
          items: [], // Empty array for Split Payments
          payment_option: 'split_payment',
          payment_option_details: {
            selectedPaymentOption: 'split_payment',
            split_payment_type: paymentData.split_payment_type,
            split_payment_text: paymentData.split_payment_text,
            isSplitPayment: true
          },
          // Include coupon information for ThankYou page
          appliedCoupon: appliedCoupon ? {
            ...appliedCoupon,
            calculated_discount_amount: couponDiscountAmount
          } : null
        };
        
        localStorage.setItem('orderDetails', JSON.stringify(orderDetails));
        console.log('💾 Split Payment order details saved to localStorage');
        
        return orderResponse.data;
      } else {
        console.error('❌ Split Payment order creation failed:', orderResponse);
        throw new Error('Failed to create Split Payment order');
      }
    } catch (error) {
      console.error('❌ Error processing Split Payment:', error);
      throw error;
    }
  };

  // Handle Cashfree payment success
  const handleCashfreeSuccess = async (data) => {
    try {
      // Check if this is an EMI installment payment
      if (hasEMIInstallmentPayments()) {
        console.log('🔄 Processing EMI installment payment via Cashfree');
        
        // Get the EMI payment data from cart
        const emiPayment = items.find(item => item.type === 'payment' && item.paymentData?.type === 'emi_payment');
        
        if (emiPayment) {
          const paymentData = {
            ...emiPayment.paymentData,
            paymentMethod: 'Cashfree Payment',
            paymentId: data.paymentId || data.orderToken
          };
          
          await processEMIInstallmentPayment(paymentData);
          
          // Clear cart and redirect to thank you page
          clearCart();
          navigate('/thank-you', { 
            state: { 
              orderNumber: emiPayment.paymentData.order_number,
              paymentMethod: 'Cashfree Payment',
              total: emiPayment.calculatedPrice,
              isEMIInstallment: true
            } 
          });
          return;
        }
      }

      // Check if this is a Split Payment
      if (items.some(item => item.type === 'split_payment')) {
        console.log('🔄 Processing Split Payment via Cashfree');
        
        // Get the Split Payment data from cart
        const splitPayment = items.find(item => item.type === 'split_payment');
        
        if (splitPayment) {
          console.log('🔍 Found Split Payment item for Cashfree:', splitPayment);
          
          const paymentData = {
            ...splitPayment.paymentData,
            paymentMethod: 'Cashfree Payment',
            paymentId: data.paymentId || data.orderToken
          };
          
          console.log('🔍 Payment data for Cashfree Split Payment:', paymentData);
          
          try {
            const orderResponse = await processSplitPayment(paymentData);
            console.log('✅ Cashfree Split Payment processed successfully, navigating to thank you page');
            
            // Clear cart and redirect to thank you page
            clearCart();
            navigate(`/thank-you?order_number=${orderResponse.data.order_number}`);
            return;
          } catch (error) {
            console.error('❌ Error in Cashfree Split Payment processing:', error);
            notify.error('Split Payment processing failed. Please try again.', 'Payment Error');
            return;
          }
        }
      }

      // Create order in database (for regular orders)
      const orderData = {
        customer_name: user?.name || 'Guest User',
        customer_email: user?.email || 'guest@example.com',
        customer_phone: user?.phone || '',
        total_amount: getPaymentAmount(),
        payment_method: 'Cashfree Payment',
        payment_id: data.paymentId || data.orderToken,
        billing_address: null,
        notes: 'Payment completed via Cashfree',
        // Payment option details
        payment_option: checkoutData?.selectedPaymentOption || 'full',
        payment_option_details: {
          selectedPaymentOption: checkoutData?.selectedPaymentOption || 'full',
          paymentOptionsConfig: paymentOptionsConfig,
          appliedCoupon: checkoutData?.paymentOptionsCoupon || null,
          ...(checkoutData?.selectedPaymentOption === 'emi' && checkoutData?.selectedEmiTenure ? {
            emi_details: {
              selectedEmiTenure: checkoutData.selectedEmiTenure,
              emiAmount: checkoutData.emiAmount,
              totalEmiAmount: checkoutData.totalEmiAmount,
              emiPrincipal: checkoutData.emiPrincipal,
              emiAdvanceAmount: checkoutData.emiAdvanceAmount
            }
          } : {})
        },
        original_total_amount: getTotal(),
        advance_amount: getPaymentAmount(),
        remaining_amount: getTotal() - getPaymentAmount(),
        items: items.map(item => {
          // Calculate individual license price based on selected license
          const licensePrice = item.selectedLicense === 'extended' 
            ? parseFloat(item.extended_license_price || 0)
            : parseFloat(item.regular_license_price || 0);
          
          return {
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
            price: licensePrice, // Individual license price
            total: licensePrice * item.quantity,
            // Include Extended Support and Addons information
            selectedLicense: item.selectedLicense,
            selectedAddons: item.selectedAddons,
            extendedSupport: item.extendedSupport,
            // Include addon details if available
            addons: item.addons,
            // Include pricing details
            regular_license_price: item.regular_license_price,
            extended_license_price: item.extended_license_price,
            extended_support_price: item.extended_support_price,
            extended_support_original_price: item.extended_support_original_price,
            extended_support_discount: item.extended_support_discount,
            regular_license_original_price: item.regular_license_original_price,
            regular_license_discount: item.regular_license_discount,
            extended_license_original_price: item.extended_license_original_price,
            extended_license_discount: item.extended_license_discount
          };
        })
      };

      const orderResponse = await orderAPI.create(orderData);
      
      if (orderResponse?.data?.success) {
        // Calculate coupon discount amount
        const couponDiscountAmount = appliedCoupon ? (() => {
          const subtotal = items.reduce((total, item) => {
            const price = item.calculatedPrice || parseFloat(item.regular_license_price || item.price || 0);
            return total + (price * item.quantity);
          }, 0);
          
          if (appliedCoupon.discount_amount && appliedCoupon.discount_amount > 0) {
            return Math.min(appliedCoupon.discount_amount, subtotal);
          } else if (appliedCoupon.discount_percentage && appliedCoupon.discount_percentage > 0) {
            return (subtotal * appliedCoupon.discount_percentage) / 100;
          }
          return 0;
        })() : 0;

        // Save order details to localStorage for thank you page
        const orderDetails = {
          orderNumber: orderResponse.data.data.order_number,
          paymentMethod: 'Cashfree Payment',
          total: getTotal(),
          payment_option: checkoutData?.selectedPaymentOption || 'full',
          payment_option_details: {
            selectedPaymentOption: checkoutData?.selectedPaymentOption || 'full',
            paymentOptionsConfig: paymentOptionsConfig,
            appliedCoupon: checkoutData?.paymentOptionsCoupon || null,
            ...(checkoutData?.selectedPaymentOption === 'emi' && checkoutData?.selectedEmiTenure ? {
              emi_details: {
                selectedEmiTenure: checkoutData.selectedEmiTenure,
                emiAmount: checkoutData.emiAmount,
                totalEmiAmount: checkoutData.totalEmiAmount,
                emiPrincipal: checkoutData.emiPrincipal,
                emiAdvanceAmount: checkoutData.emiAdvanceAmount
              }
            } : {})
          },
          original_total_amount: getTotal(),
          advance_amount: getPaymentAmount(),
          remaining_amount: getTotal() - getPaymentAmount(),
          items: items.map(item => {
            // Calculate individual license price based on selected license
            const licensePrice = item.selectedLicense === 'extended' 
              ? parseFloat(item.extended_license_price || 0)
              : parseFloat(item.regular_license_price || 0);
            
            return {
              product_id: item.id,
              product_name: item.name,
              quantity: item.quantity,
              price: licensePrice, // Individual license price
              total: licensePrice * item.quantity,
              // Include Extended Support and Addons information
              selectedLicense: item.selectedLicense,
              selectedAddons: item.selectedAddons,
              extendedSupport: item.extendedSupport,
              // Include addon details if available
              addons: item.addons,
              // Include pricing details
              regular_license_price: item.regular_license_price,
              extended_license_price: item.extended_license_price,
              extended_support_price: item.extended_support_price,
              extended_support_original_price: item.extended_support_original_price,
              extended_support_discount: item.extended_support_discount,
              regular_license_original_price: item.regular_license_original_price,
              regular_license_discount: item.regular_license_discount,
              extended_license_original_price: item.extended_license_original_price,
              extended_license_discount: item.extended_license_discount
            };
          }),
          date: new Date().toISOString(),
          saveDetails,
          paymentStatus: data.paymentStatus || orderResponse.data.data.payment_status || 'pending',
          orderStatus: data.orderStatus || orderResponse.data.data.order_status || 'processing',
          paymentId: data.paymentId || data.orderToken,
          orderId: orderResponse.data.data.order_id,
          // Include coupon information with calculated discount amount
          appliedCoupon: appliedCoupon ? {
            ...appliedCoupon,
            calculated_discount_amount: couponDiscountAmount
          } : null
        };

        // Update user profile with address and WhatsApp if provided and user is authenticated
        if (user && (checkoutData?.address || checkoutData?.whatsapp || codFormData.whatsapp || offlineFormData.whatsapp)) {
          try {
            const profileUpdateData = {};
            if (checkoutData?.address && checkoutData.address !== user.address) {
              profileUpdateData.address = checkoutData.address;
            }
            // Check WhatsApp from various sources
            const whatsappToSave = checkoutData?.whatsapp || codFormData.whatsapp || offlineFormData.whatsapp;
            if (whatsappToSave && whatsappToSave !== user.whatsapp) {
              profileUpdateData.whatsapp = whatsappToSave;
            }
            
            if (Object.keys(profileUpdateData).length > 0) {
              await api.put('/users/profile', profileUpdateData);
            }
          } catch (error) {
            console.error('Error updating user profile:', error);
            // Don't fail the order if profile update fails
          }
        }

        // Update order status to completed if payment was successful
        if (data.paymentStatus === 'completed') {
          try {
            await api.put(`/orders/${orderResponse.data.data.order_number}/status`, {
              payment_status: 'completed',
              order_status: 'processing',
              payment_id: data.paymentId || data.orderToken
            });
            console.log('✅ Order status updated to completed');
          } catch (updateError) {
            console.error('❌ Failed to update order status:', updateError);
          }
        }

        localStorage.setItem('orderDetails', JSON.stringify(orderDetails));

        // Clear cart
        clearCart();

        // Navigate to thank you page immediately
        navigate('/thank-you', { replace: true });
      } else {
        throw new Error('Failed to create order');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      notify.error('Payment successful but order creation failed. Please contact support.', 'Order Creation Error');
    }
  };

  // Handle Cashfree payment error
  const handleCashfreeError = async (error) => {
    console.error('Cashfree payment failed or was cancelled:', error);
    
    // Create order with failed status
    try {
      const orderData = {
        customer_name: user?.name || 'Guest User',
        customer_email: user?.email || 'guest@example.com',
        customer_phone: user?.phone || '',
        total_amount: getPaymentAmount(),
        payment_method: 'Cashfree Payment',
        payment_id: error.paymentId || error.orderToken || 'FAILED_PAYMENT_' + Date.now(),
        billing_address: null,
        notes: error.message && error.message.includes('cancelled') 
          ? 'Payment failed - user cancelled or left without completing payment via Cashfree' 
          : 'Payment failed via Cashfree',
        // Payment option details
        payment_option: checkoutData?.selectedPaymentOption || 'full',
        payment_option_details: {
          selectedPaymentOption: checkoutData?.selectedPaymentOption || 'full',
          paymentOptionsConfig: paymentOptionsConfig,
          appliedCoupon: checkoutData?.paymentOptionsCoupon || null,
          ...(checkoutData?.selectedPaymentOption === 'emi' && checkoutData?.selectedEmiTenure ? {
            emi_details: {
              selectedEmiTenure: checkoutData.selectedEmiTenure,
              emiAmount: checkoutData.emiAmount,
              totalEmiAmount: checkoutData.totalEmiAmount,
              emiPrincipal: checkoutData.emiPrincipal,
              emiAdvanceAmount: checkoutData.emiAdvanceAmount
            }
          } : {})
        },
        original_total_amount: getTotal(),
        advance_amount: getPaymentAmount(),
        remaining_amount: getTotal() - getPaymentAmount(),
        items: items.map(item => {
          const licensePrice = item.selectedLicense === 'extended' 
            ? parseFloat(item.extended_license_price || 0)
            : parseFloat(item.regular_license_price || 0);
          
          return {
            product_id: item.id,
            product_name: item.name,
            quantity: item.quantity,
            price: licensePrice,
            total: licensePrice * item.quantity,
            selectedLicense: item.selectedLicense,
            selectedAddons: item.selectedAddons,
            extendedSupport: item.extendedSupport,
            addons: item.addons,
            regular_license_price: item.regular_license_price,
            extended_license_price: item.extended_license_price,
            extended_support_price: item.extended_support_price,
            extended_support_original_price: item.extended_support_original_price,
            extended_support_discount: item.extended_support_discount,
            regular_license_original_price: item.regular_license_original_price,
            regular_license_discount: item.regular_license_discount,
            extended_license_original_price: item.extended_license_original_price,
            extended_license_discount: item.extended_license_discount
          };
        })
      };

      const orderResponse = await orderAPI.create(orderData);
      
      if (orderResponse?.data?.success) {
        // Save order details to localStorage for thank you page
        const orderDetails = {
          orderNumber: orderResponse.data.data.order_number,
          paymentMethod: 'Cashfree Payment',
          total: getTotal(),
          payment_option: checkoutData?.selectedPaymentOption || 'full',
          payment_option_details: {
            selectedPaymentOption: checkoutData?.selectedPaymentOption || 'full',
            paymentOptionsConfig: paymentOptionsConfig,
            appliedCoupon: checkoutData?.paymentOptionsCoupon || null,
            ...(checkoutData?.selectedPaymentOption === 'emi' && checkoutData?.selectedEmiTenure ? {
              emi_details: {
                selectedEmiTenure: checkoutData.selectedEmiTenure,
                emiAmount: checkoutData.emiAmount,
                totalEmiAmount: checkoutData.totalEmiAmount,
                emiPrincipal: checkoutData.emiPrincipal,
                emiAdvanceAmount: checkoutData.emiAdvanceAmount
              }
            } : {})
          },
          original_total_amount: getTotal(),
          advance_amount: getPaymentAmount(),
          remaining_amount: getTotal() - getPaymentAmount(),
          items: items.map(item => {
            const licensePrice = item.selectedLicense === 'extended' 
              ? parseFloat(item.extended_license_price || 0)
              : parseFloat(item.regular_license_price || 0);
            
            return {
              product_id: item.id,
              product_name: item.name,
              quantity: item.quantity,
              price: licensePrice,
              total: licensePrice * item.quantity,
              selectedLicense: item.selectedLicense,
              selectedAddons: item.selectedAddons,
              extendedSupport: item.extendedSupport,
              addons: item.addons,
              regular_license_price: item.regular_license_price,
              extended_license_price: item.extended_license_price,
              extended_support_price: item.extended_support_price,
              extended_support_original_price: item.extended_support_original_price,
              extended_support_discount: item.extended_support_discount,
              regular_license_original_price: item.regular_license_original_price,
              regular_license_discount: item.regular_license_discount,
              extended_license_original_price: item.extended_license_original_price,
              extended_license_discount: item.extended_license_discount
            };
          }),
          date: new Date().toISOString(),
          saveDetails,
          paymentStatus: error.paymentStatus || 'failed',
          orderStatus: error.orderStatus || 'failed',
          paymentId: error.paymentId || error.orderToken,
          orderId: orderResponse.data.data.order_id,
          appliedCoupon: appliedCoupon ? {
            ...appliedCoupon,
            calculated_discount_amount: 0
          } : null
        };

        // Update user profile with address and WhatsApp if provided and user is authenticated
        if (user && (checkoutData?.address || checkoutData?.whatsapp || codFormData.whatsapp || offlineFormData.whatsapp)) {
          try {
            const profileUpdateData = {};
            if (checkoutData?.address && checkoutData.address !== user.address) {
              profileUpdateData.address = checkoutData.address;
            }
            // Check WhatsApp from various sources
            const whatsappToSave = checkoutData?.whatsapp || codFormData.whatsapp || offlineFormData.whatsapp;
            if (whatsappToSave && whatsappToSave !== user.whatsapp) {
              profileUpdateData.whatsapp = whatsappToSave;
            }
            
            if (Object.keys(profileUpdateData).length > 0) {
              await api.put('/users/profile', profileUpdateData);
            }
          } catch (error) {
            console.error('Error updating user profile:', error);
            // Don't fail the order if profile update fails
          }
        }

        // Update order status to failed
        try {
          await api.put(`/orders/${orderResponse.data.data.order_number}/status`, {
            payment_status: 'failed',
            order_status: 'failed',
            payment_id: error.paymentId || error.orderToken
          });
          console.log('❌ Order status updated to failed');
        } catch (updateError) {
          console.error('❌ Failed to update order status:', updateError);
        }

        localStorage.setItem('orderDetails', JSON.stringify(orderDetails));

        // Clear cart
        clearCart();

        // Navigate to thank you page
        navigate('/thank-you', { replace: true });
      } else {
        throw new Error('Failed to create order');
      }
    } catch (orderError) {
      console.error('Error creating failed order:', orderError);
      notify.error('Payment failed. Please try again.', 'Payment Error');
    }
    
    setShowCashfreePayment(false);
  };

  // Handle Cashfree payment cancel (this function is now only used for explicit cancellations)
  const handleCashfreeCancel = async (cancelData = null) => {
    console.log('🚫 Cashfree payment was cancelled by user', cancelData);
    
    // Create order with cancelled status
    try {
      const orderData = {
        customer_name: user?.name || 'Guest User',
        customer_email: user?.email || 'guest@example.com',
        customer_phone: user?.phone || '',
        total_amount: getPaymentAmount(),
        payment_method: 'Cashfree Payment',
        payment_id: 'CANCELLED_PAYMENT_' + Date.now(),
        billing_address: null,
        notes: 'Payment cancelled by user via Cashfree',
        // Payment option details
        payment_option: checkoutData?.selectedPaymentOption || 'full',
        payment_option_details: {
          selectedPaymentOption: checkoutData?.selectedPaymentOption || 'full',
          paymentOptionsConfig: paymentOptionsConfig,
          appliedCoupon: checkoutData?.paymentOptionsCoupon || null,
          ...(checkoutData?.selectedPaymentOption === 'emi' && checkoutData?.selectedEmiTenure ? {
            emi_details: {
              selectedEmiTenure: checkoutData.selectedEmiTenure,
              emiAmount: checkoutData.emiAmount,
              totalEmiAmount: checkoutData.totalEmiAmount,
              emiPrincipal: checkoutData.emiPrincipal,
              emiAdvanceAmount: checkoutData.emiAdvanceAmount
            }
          } : {})
        },
        original_total_amount: getTotal(),
        advance_amount: getPaymentAmount(),
        remaining_amount: getTotal() - getPaymentAmount(),
        items: items.map(item => {
          const licensePrice = item.selectedLicense === 'extended' 
            ? parseFloat(item.extended_license_price || 0)
            : parseFloat(item.regular_license_price || 0);
          
          return {
            product_id: item.id,
            product_name: item.name,
            quantity: item.quantity,
            price: licensePrice,
            total: licensePrice * item.quantity,
            selectedLicense: item.selectedLicense,
            selectedAddons: item.selectedAddons,
            extendedSupport: item.extendedSupport,
            addons: item.addons,
            regular_license_price: item.regular_license_price,
            extended_license_price: item.extended_license_price,
            extended_support_price: item.extended_support_price,
            extended_support_original_price: item.extended_support_original_price,
            extended_support_discount: item.extended_support_discount,
            regular_license_original_price: item.regular_license_original_price,
            regular_license_discount: item.regular_license_discount,
            extended_license_original_price: item.extended_license_original_price,
            extended_license_discount: item.extended_license_discount
          };
        })
      };

      const orderResponse = await orderAPI.create(orderData);
      
      if (orderResponse?.data?.success) {
        // Save order details to localStorage for thank you page
        const orderDetails = {
          orderNumber: orderResponse.data.data.order_number,
          paymentMethod: 'Cashfree Payment',
          total: getTotal(),
          payment_option: checkoutData?.selectedPaymentOption || 'full',
          payment_option_details: {
            selectedPaymentOption: checkoutData?.selectedPaymentOption || 'full',
            paymentOptionsConfig: paymentOptionsConfig,
            appliedCoupon: checkoutData?.paymentOptionsCoupon || null,
            ...(checkoutData?.selectedPaymentOption === 'emi' && checkoutData?.selectedEmiTenure ? {
              emi_details: {
                selectedEmiTenure: checkoutData.selectedEmiTenure,
                emiAmount: checkoutData.emiAmount,
                totalEmiAmount: checkoutData.totalEmiAmount,
                emiPrincipal: checkoutData.emiPrincipal,
                emiAdvanceAmount: checkoutData.emiAdvanceAmount
              }
            } : {})
          },
          original_total_amount: getTotal(),
          advance_amount: getPaymentAmount(),
          remaining_amount: getTotal() - getPaymentAmount(),
          items: items.map(item => {
            const licensePrice = item.selectedLicense === 'extended' 
              ? parseFloat(item.extended_license_price || 0)
              : parseFloat(item.regular_license_price || 0);
            
            return {
              product_id: item.id,
              product_name: item.name,
              quantity: item.quantity,
              price: licensePrice,
              total: licensePrice * item.quantity,
              selectedLicense: item.selectedLicense,
              selectedAddons: item.selectedAddons,
              extendedSupport: item.extendedSupport,
              addons: item.addons,
              regular_license_price: item.regular_license_price,
              extended_license_price: item.extended_license_price,
              extended_support_price: item.extended_support_price,
              extended_support_original_price: item.extended_support_original_price,
              extended_support_discount: item.extended_support_discount,
              regular_license_original_price: item.regular_license_original_price,
              regular_license_discount: item.regular_license_discount,
              extended_license_original_price: item.extended_license_original_price,
              extended_license_discount: item.extended_license_discount
            };
          }),
          date: new Date().toISOString(),
          saveDetails,
          paymentStatus: cancelData?.paymentStatus || 'cancelled',
          orderStatus: cancelData?.orderStatus || 'cancelled',
          paymentId: cancelData?.data?.paymentId || 'CANCELLED_PAYMENT_' + Date.now(),
          orderId: orderResponse.data.data.order_id,
          appliedCoupon: appliedCoupon ? {
            ...appliedCoupon,
            calculated_discount_amount: 0
          } : null
        };

        // Update user profile with address and WhatsApp if provided and user is authenticated
        if (user && (checkoutData?.address || checkoutData?.whatsapp || codFormData.whatsapp || offlineFormData.whatsapp)) {
          try {
            const profileUpdateData = {};
            if (checkoutData?.address && checkoutData.address !== user.address) {
              profileUpdateData.address = checkoutData.address;
            }
            // Check WhatsApp from various sources
            const whatsappToSave = checkoutData?.whatsapp || codFormData.whatsapp || offlineFormData.whatsapp;
            if (whatsappToSave && whatsappToSave !== user.whatsapp) {
              profileUpdateData.whatsapp = whatsappToSave;
            }
            
            if (Object.keys(profileUpdateData).length > 0) {
              await api.put('/users/profile', profileUpdateData);
            }
          } catch (error) {
            console.error('Error updating user profile:', error);
            // Don't fail the order if profile update fails
          }
        }

        // Update order status to cancelled
        try {
          await api.put(`/orders/${orderResponse.data.data.order_number}/status`, {
            payment_status: 'cancelled',
            order_status: 'cancelled',
            payment_id: cancelData?.data?.paymentId || 'CANCELLED_PAYMENT_' + Date.now()
          });
          console.log('🚫 Order status updated to cancelled');
        } catch (updateError) {
          console.error('❌ Failed to update order status:', updateError);
        }

        localStorage.setItem('orderDetails', JSON.stringify(orderDetails));

        // Clear cart
        clearCart();

        // Navigate to thank you page
        navigate('/thank-you', { replace: true });
      } else {
        throw new Error('Failed to create order');
      }
    } catch (orderError) {
      console.error('Error creating failed order:', orderError);
      notify.error('Payment failed. You can try again.', 'Payment Error');
    }
    
    setShowCashfreePayment(false);
  };

  const handleConfirmPayment = async () => {
    if (!selectedPayment) {
      notify.warning('Please select a payment method', 'Payment Method Required');
      return;
    }

    // For offline payment, require payment proof upload and form data
    if (selectedPayment === 'offline') {
      if (!offlineProof) {
        notify.warning('Please upload payment proof to continue', 'Payment Proof Required');
        return;
      }
      if (!offlineFormData.name || !offlineFormData.email || !offlineFormData.phone || !offlineFormData.whatsapp) {
        notify.warning('Please fill in all required fields (Name, Email, Phone Number, WhatsApp Number)', 'Required Fields Missing');
        return;
      }
    }

    // For COD, require form data
    if (selectedPayment === 'cod') {
      if (!codFormData.name || !codFormData.email || !codFormData.phone || !codFormData.whatsapp) {
        notify.warning('Please fill in all required fields (Name, Email, Phone Number, WhatsApp Number)', 'Required Fields Missing');
        return;
      }
    }

    // For Cashfree, the payment is handled by the CashfreePayment component
    if (selectedPayment === 'cashfree') {
      setShowCashfreePayment(true);
      return;
    }

    setLoading(true);

    try {
      // Check if this is an EMI installment payment
      if (hasEMIInstallmentPayments()) {
        console.log('🔄 Processing EMI installment payment via', selectedPayment);
        
        // Get the EMI payment data from cart
        const emiPayment = items.find(item => item.type === 'payment' && item.paymentData?.type === 'emi_payment');
        
        if (emiPayment) {
          const paymentData = {
            ...emiPayment.paymentData,
            paymentMethod: paymentMethods.find(pm => pm.id === selectedPayment)?.name || selectedPayment
          };
          
          await processEMIInstallmentPayment(paymentData);
          
          // Clear cart and redirect to thank you page
          clearCart();
          navigate('/thank-you', { 
            state: { 
              orderNumber: emiPayment.paymentData.order_number,
              paymentMethod: paymentMethods.find(pm => pm.id === selectedPayment)?.name || selectedPayment,
              total: emiPayment.calculatedPrice,
              isEMIInstallment: true
            } 
          });
          return;
        }
      }

      // Check if this is a Split Payment
      if (items.some(item => item.type === 'split_payment')) {
        console.log('🔄 Processing Split Payment via', selectedPayment);
        
        // Get the Split Payment data from cart
        const splitPayment = items.find(item => item.type === 'split_payment');
        
        if (splitPayment) {
          console.log('🔍 Found Split Payment item:', splitPayment);
          
          const paymentData = {
            ...splitPayment.paymentData,
            paymentMethod: paymentMethods.find(pm => pm.id === selectedPayment)?.name || selectedPayment
          };
          
          console.log('🔍 Payment data for Split Payment:', paymentData);
          
          try {
            const orderResponse = await processSplitPayment(paymentData);
            console.log('✅ Split Payment processed successfully, navigating to thank you page');
            
            // Clear cart and redirect to thank you page
            clearCart();
            navigate(`/thank-you?order_number=${orderResponse.data.order_number}`);
            return;
          } catch (error) {
            console.error('❌ Error in Split Payment processing:', error);
            notify.error('Split Payment processing failed. Please try again.', 'Payment Error');
            setLoading(false);
            return;
          }
        }
      }
      // Upload payment proof file if offline payment
      let uploadedFileData = null;
      if (selectedPayment === 'offline' && offlineProof) {
        try {
          const uploadResponse = await uploadAPI.uploadDocument(offlineProof);
          if (uploadResponse?.data?.success) {
            uploadedFileData = uploadResponse.data.data;
          }
        } catch (uploadError) {
          console.error('Payment proof upload failed:', uploadError);
          notify.error('Payment proof upload failed. Please try again.', 'Upload Error');
          setLoading(false);
          return;
        }
      }

      const orderData = {
        customer_name: selectedPayment === 'cod' ? codFormData.name : (selectedPayment === 'offline' ? offlineFormData.name : (user?.name || 'Guest User')),
        customer_email: selectedPayment === 'cod' ? codFormData.email : (selectedPayment === 'offline' ? offlineFormData.email : (user?.email || 'guest@example.com')),
        customer_phone: selectedPayment === 'cod' ? codFormData.phone : (selectedPayment === 'offline' ? offlineFormData.phone : (user?.phone || '')),
        customer_whatsapp: selectedPayment === 'cod' ? codFormData.whatsapp : (selectedPayment === 'offline' ? offlineFormData.whatsapp : ''),
        total_amount: getPaymentAmount(),
        payment_method: paymentMethods.find(pm => pm.id === selectedPayment)?.name,
        payment_id: null,
        billing_address: null,
        notes: `Payment method: ${paymentMethods.find(pm => pm.id === selectedPayment)?.name}${selectedPayment === 'cod' ? ` | Contact: ${codFormData.name} (${codFormData.phone})` : ''}${selectedPayment === 'offline' ? ` | Contact: ${offlineFormData.name} (${offlineFormData.phone})` : ''}`,
        // Payment option details
        payment_option: checkoutData?.selectedPaymentOption || 'full',
        payment_option_details: {
          selectedPaymentOption: checkoutData?.selectedPaymentOption || 'full',
          paymentOptionsConfig: paymentOptionsConfig,
          appliedCoupon: checkoutData?.paymentOptionsCoupon || null,
          ...(checkoutData?.selectedPaymentOption === 'emi' && checkoutData?.selectedEmiTenure ? {
            emi_details: {
              selectedEmiTenure: checkoutData.selectedEmiTenure,
              emiAmount: checkoutData.emiAmount,
              totalEmiAmount: checkoutData.totalEmiAmount,
              emiPrincipal: checkoutData.emiPrincipal,
              emiAdvanceAmount: checkoutData.emiAdvanceAmount
            }
          } : {})
        },
        original_total_amount: getTotal(),
        advance_amount: getPaymentAmount(),
        remaining_amount: getTotal() - getPaymentAmount(),
        payment_proof: uploadedFileData ? {
          filename: uploadedFileData.filename,
          originalName: uploadedFileData.originalName,
          size: uploadedFileData.size,
          type: uploadedFileData.mimeType,
          uploaded_at: new Date().toISOString(),
          file_path: uploadedFileData.path,
          file_url: uploadedFileData.url,
          file_id: uploadedFileData.fileId
        } : null,
        // For installment payments, do not send order items to avoid FK constraint
        items: items.some(i => i.type === 'payment') ? [] : items.map(item => {
          const licensePrice = item.selectedLicense === 'extended' 
            ? parseFloat(item.extended_license_price || 0)
            : parseFloat(item.regular_license_price || 0);
          return {
            product_id: item.id,
            product_name: item.name,
            quantity: item.quantity,
            price: licensePrice,
            total: licensePrice * item.quantity,
            selectedLicense: item.selectedLicense,
            selectedAddons: item.selectedAddons,
            extendedSupport: item.extendedSupport,
            addons: item.addons,
            regular_license_price: item.regular_license_price,
            extended_license_price: item.extended_license_price,
            extended_support_price: item.extended_support_price,
            extended_support_original_price: item.extended_support_original_price,
            extended_support_discount: item.extended_support_discount,
            regular_license_original_price: item.regular_license_original_price,
            regular_license_discount: item.regular_license_discount,
            extended_license_original_price: item.extended_license_original_price,
            extended_license_discount: item.extended_license_discount
          };
        })
      };

      // Check if this is a payment item (EMI/Split payment installment)
      const hasPaymentItems = items.some(item => item.type === 'payment');
      const paymentItem = hasPaymentItems ? items.find(item => item.type === 'payment') : null;
      
      let orderResponse;
      
      // For both payment items and regular items, create a regular order
      // but add special handling for payment items in the order data
      if (hasPaymentItems) {
        // Add payment-specific information to the order data
        orderData.payment_type = 'installment';
        orderData.original_payment_transaction_id = paymentItem.paymentData.transaction_id;
        orderData.installment_details = {
          transaction_type: paymentItem.paymentData.type,
          original_order_id: paymentItem.paymentData.order_id,
          next_payment_date: paymentItem.paymentData.next_payment_date,
          next_payment_amount: paymentItem.paymentData.next_payment_amount,
          total_installments: paymentItem.paymentData.total_installments,
          current_installment: paymentItem.paymentData.current_installment,
          remaining_amount: paymentItem.paymentData.remaining_amount
        };
      }
      
      // Create order (works for both payment items and regular items)
      orderResponse = await orderAPI.create(orderData);
      
      if (orderResponse?.data?.success) {
        // Calculate coupon discount amount
        const couponDiscountAmount = appliedCoupon ? (() => {
          const subtotal = items.reduce((total, item) => {
            // Handle payment items
            if (item.type === 'payment') {
              const price = parseFloat(item.price || 0);
              return total + (price * item.quantity);
            }
            // Handle regular product items
            const price = item.calculatedPrice || parseFloat(item.regular_license_price || item.price || 0);
            return total + (price * item.quantity);
          }, 0);
          
          if (appliedCoupon.discount_amount && appliedCoupon.discount_amount > 0) {
            return Math.min(appliedCoupon.discount_amount, subtotal);
          } else if (appliedCoupon.discount_percentage && appliedCoupon.discount_percentage > 0) {
            return (subtotal * appliedCoupon.discount_percentage) / 100;
          }
          return 0;
        })() : 0;

        // Save order details to localStorage for thank you page
        const orderDetails = {
          orderNumber: orderResponse.data.data.order_number,
          paymentMethod: paymentMethods.find(pm => pm.id === selectedPayment)?.name,
          total: getPaymentAmount(),
          payment_option: hasPaymentItems ? 'installment' : (checkoutData?.selectedPaymentOption || 'full'),
          payment_option_details: hasPaymentItems ? {
            selectedPaymentOption: 'installment',
            paymentData: paymentItem.paymentData,
            isInstallmentPayment: true,
            installment_details: orderData.installment_details
          } : {
            selectedPaymentOption: checkoutData?.selectedPaymentOption || 'full',
            paymentOptionsConfig: paymentOptionsConfig,
            appliedCoupon: checkoutData?.paymentOptionsCoupon || null,
            ...(checkoutData?.selectedPaymentOption === 'emi' && checkoutData?.selectedEmiTenure ? {
              emi_details: {
                selectedEmiTenure: checkoutData.selectedEmiTenure,
                emiAmount: checkoutData.emiAmount,
                totalEmiAmount: checkoutData.totalEmiAmount,
                emiPrincipal: checkoutData.emiPrincipal,
                emiAdvanceAmount: checkoutData.emiAdvanceAmount
              }
            } : {})
          },
          original_total_amount: hasPaymentItems ? paymentItem.paymentData.total_amount : getTotal(),
          advance_amount: getPaymentAmount(),
          remaining_amount: hasPaymentItems ? paymentItem.paymentData.remaining_amount : (getTotal() - getPaymentAmount()),
          // For installment payments, do not send order items to avoid FK constraint
          items: items.some(i => i.type === 'payment') ? [] : items.map(item => {
            const licensePrice = item.selectedLicense === 'extended' 
              ? parseFloat(item.extended_license_price || 0)
              : parseFloat(item.regular_license_price || 0);
            return {
              product_id: item.id,
              product_name: item.name,
              quantity: item.quantity,
              price: licensePrice, // Individual license price
              total: licensePrice * item.quantity,
              // Include Extended Support and Addons information
              selectedLicense: item.selectedLicense,
              selectedAddons: item.selectedAddons,
              extendedSupport: item.extendedSupport,
              // Include addon details if available
              addons: item.addons,
              // Include pricing details
              regular_license_price: item.regular_license_price,
              extended_license_price: item.extended_license_price,
              extended_support_price: item.extended_support_price,
              extended_support_original_price: item.extended_support_original_price,
              extended_support_discount: item.extended_support_discount,
              regular_license_original_price: item.regular_license_original_price,
              regular_license_discount: item.regular_license_discount,
              extended_license_original_price: item.extended_license_original_price,
              extended_license_discount: item.extended_license_discount
            };
          }),
          date: new Date().toISOString(),
          saveDetails,
          paymentStatus: orderResponse.data.data.payment_status || (selectedPayment === 'offline' ? 'pending' : 'processing'),
          orderStatus: orderResponse.data.data.order_status || 'pending',
          offlineProof: offlineProof ? offlineProof.name : null,
          orderId: orderResponse.data.data.order_id,
          // Include coupon information with calculated discount amount
          appliedCoupon: appliedCoupon ? {
            ...appliedCoupon,
            calculated_discount_amount: couponDiscountAmount
          } : null
        };

        localStorage.setItem('orderDetails', JSON.stringify(orderDetails));

        // Update user profile with address and WhatsApp if provided and user is authenticated
        if (user && (checkoutData?.address || checkoutData?.whatsapp || codFormData.whatsapp || offlineFormData.whatsapp)) {
          try {
            const profileUpdateData = {};
            if (checkoutData?.address && checkoutData.address !== user.address) {
              profileUpdateData.address = checkoutData.address;
            }
            // Check WhatsApp from various sources
            const whatsappToSave = checkoutData?.whatsapp || codFormData.whatsapp || offlineFormData.whatsapp;
            if (whatsappToSave && whatsappToSave !== user.whatsapp) {
              profileUpdateData.whatsapp = whatsappToSave;
            }
            
            if (Object.keys(profileUpdateData).length > 0) {
              await api.put('/users/profile', profileUpdateData);
            }
          } catch (error) {
            console.error('Error updating user profile:', error);
            // Don't fail the order if profile update fails
          }
        }

        // Clear cart
        clearCart();

        // Navigate to thank you page immediately
        navigate('/thank-you', { replace: true });
      } else {
        throw new Error('Failed to create order');
      }
    } catch (error) {
      console.error('Payment failed:', error);
      notify.error('Payment failed. Please try again.', 'Payment Error');
    } finally {
      setLoading(false);
    }
  };

  // If Cashfree payment is active, show the CashfreePayment component
  if (showCashfreePayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
        <ProgressBar currentStep="payment" />
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-12 sm:py-16">
          <div className="mb-8">
            <button
              onClick={() => setShowCashfreePayment(false)}
              className="flex items-center space-x-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back to Payment Methods</span>
            </button>
          </div>
          <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-6 sm:p-8 md:p-12">
          {(() => {
            const totalAmount = getPaymentAmount();
            console.log('💰 Payment page amount:', {
              totalAmount: totalAmount,
              type: typeof totalAmount,
              subtotal: getSubtotal(),
              discount: getDiscount(),
              paymentOption: checkoutData?.selectedPaymentOption,
              originalTotal: getTotal()
            });
            return (
              <CashfreePayment
                amount={totalAmount}
                currency="INR"
                orderId={'ORD-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9).toUpperCase()}
                customerInfo={{
                  name: user?.name || 'Guest User',
                  email: user?.email || 'guest@example.com',
                  phone: user?.phone || '+91-9876543210'
                }}
                onSuccess={handleCashfreeSuccess}
                onError={handleCashfreeError}
                onCancel={handleCashfreeCancel}
              />
            );
          })()}
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
        <ProgressBar currentStep="payment" />
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg">
              <ShoppingCart className="w-16 h-16 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              No items in cart
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-12">
              Please add some products to your cart before proceeding to payment.
            </p>
            <button
              onClick={() => navigate('/products')}
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow"
            >
              Browse Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
      <ProgressBar currentStep="payment" />
      
      <div className="py-12">
        <div className="container mx-auto px-4 sm:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8 md:p-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Select Payment Method
          </h2>

          {/* Payment Methods Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
            {(() => {
              const enabledPaymentMethods = paymentMethods.filter(method => method.enabled);
              
              if (enabledPaymentMethods.length === 0) {
                return (
                  <div className="col-span-full">
                        <div className="text-center py-16 px-8 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20">
                          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                            <CreditCard className="w-12 h-12 text-white" />
                      </div>
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        No Payment Methods Available
                      </h3>
                          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto">
                        No payment methods are currently available. Please try again later or contact our support team for assistance.
                      </p>
                          <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                          onClick={handleBackToCheckout}
                              className="card-3d bg-gradient-to-r from-gray-600 to-gray-700 text-white px-8 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow"
                        >
                              <ArrowLeft className="w-5 h-5 mr-2 inline" />
                          Back to Checkout
                        </button>
                        <button
                          onClick={() => navigate('/contact')}
                              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow"
                        >
                          Contact Support
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }
              
              return enabledPaymentMethods.map((method) => {
              const Icon = method.icon;
              const isSelected = selectedPayment === method.id;
              
              return (
                <div
                  key={method.id}
                  onClick={() => handlePaymentSelection(method.id)}
                      className={`card-3d p-6 border-2 rounded-3xl cursor-pointer transition-all duration-500 hover:scale-105 ${
                    isSelected
                          ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 shadow-glow'
                          : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 bg-white/80 dark:bg-white/10 backdrop-blur-md shadow-xl hover:shadow-2xl'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`p-3 rounded-2xl ${method.color} bg-gradient-to-br from-white to-gray-100 dark:from-gray-800 dark:to-gray-700 shadow-lg`}>
                          <Icon className="w-8 h-8" />
                    </div>
                    <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                        {method.name}
                      </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                        {method.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
              });
            })()}
          </div>

          {/* Offline Payment Details */}
          {showOfflineDetails && paymentConfig?.offline && (
                <div className="mb-8 card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md rounded-3xl shadow-xl border border-green-200 dark:border-green-800 p-8">
                  <h3 className="text-2xl font-bold text-green-900 dark:text-green-100 mb-6 flex items-center gap-3">
                    <Building2 className="w-8 h-8" />
                Offline Payment Details
              </h3>
              
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                {/* Bank Details */}
                {paymentConfig.offline.bank_details && (
                      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                        <h4 className="text-lg font-bold text-green-800 dark:text-green-200 mb-4">Bank Transfer Details</h4>
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                            <span className="font-semibold text-green-700 dark:text-green-300">Account Name:</span>
                            <span className="text-green-900 dark:text-green-100">{paymentConfig.offline.bank_details.account_name}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                            <span className="font-semibold text-green-700 dark:text-green-300">Account Number:</span>
                            <span className="text-green-900 dark:text-green-100">{paymentConfig.offline.bank_details.account_number}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                            <span className="font-semibold text-green-700 dark:text-green-300">IFSC Code:</span>
                            <span className="text-green-900 dark:text-green-100">{paymentConfig.offline.bank_details.ifsc_code}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                            <span className="font-semibold text-green-700 dark:text-green-300">Bank:</span>
                            <span className="text-green-900 dark:text-green-100">{paymentConfig.offline.bank_details.bank_name}</span>
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <span className="font-semibold text-green-700 dark:text-green-300">Branch:</span>
                            <span className="text-green-900 dark:text-green-100">{paymentConfig.offline.bank_details.branch}</span>
                          </div>
                    </div>
                  </div>
                )}

                {/* UPI Details */}
                {paymentConfig.offline.upi_details && paymentConfig.offline.upi_details.upi_id && (
                      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                        <h4 className="text-lg font-bold text-green-800 dark:text-green-200 mb-4">UPI Payment Details</h4>
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                            <span className="font-semibold text-green-700 dark:text-green-300">UPI ID:</span>
                            <span className="text-green-900 dark:text-green-100">{paymentConfig.offline.upi_details.upi_id}</span>
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <span className="font-semibold text-green-700 dark:text-green-300">UPI Name:</span>
                            <span className="text-green-900 dark:text-green-100">{paymentConfig.offline.upi_details.upi_name}</span>
                          </div>
                    </div>
                  </div>
                )}

                {/* QR Code */}
                {paymentConfig.offline.qr_code_image && (
                      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                        <h4 className="text-lg font-bold text-green-800 dark:text-green-200 mb-4">QR Code</h4>
                    <div className="flex justify-center">
                      <img 
                        src={paymentConfig.offline.qr_code_image} 
                        alt="Payment QR Code" 
                            className="max-w-xs border-2 border-green-300 dark:border-green-600 rounded-2xl p-4 bg-white shadow-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="text-center text-sm text-gray-500 dark:text-gray-400" style={{display: 'none'}}>
                        Failed to load QR code
                      </div>
                    </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-3">
                      Scan this QR code to make payment
                    </p>
                  </div>
                )}
              </div>

              {/* Payment Instructions */}
              {paymentConfig.offline.payment_instructions && (
                    <div className="mt-6 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                      <h4 className="text-lg font-bold text-green-800 dark:text-green-200 mb-4">Payment Instructions</h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">
                    {paymentConfig.offline.payment_instructions}
                  </p>
                </div>
              )}

              {/* Contact Information */}
              {paymentConfig.offline.contact_info && (
                    <div className="mt-6 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                      <h4 className="text-lg font-bold text-green-800 dark:text-green-200 mb-4">Contact Information</h4>
                      <div className="space-y-3 text-sm">
                    {paymentConfig.offline.contact_info.phone && (
                          <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                            <span className="font-semibold text-green-700 dark:text-green-300">Phone:</span>
                            <span className="text-green-900 dark:text-green-100">{paymentConfig.offline.contact_info.phone}</span>
                          </div>
                    )}
                    {paymentConfig.offline.contact_info.email && (
                          <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                            <span className="font-semibold text-green-700 dark:text-green-300">Email:</span>
                            <span className="text-green-900 dark:text-green-100">{paymentConfig.offline.contact_info.email}</span>
                          </div>
                    )}
                    {paymentConfig.offline.contact_info.address && (
                          <div className="flex justify-between items-center py-2">
                            <span className="font-semibold text-green-700 dark:text-green-300">Address:</span>
                            <span className="text-green-900 dark:text-green-100">{paymentConfig.offline.contact_info.address}</span>
                          </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Proof Upload */}
                  <div className="mt-8 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <h4 className="text-lg font-bold text-green-800 dark:text-green-200 mb-4">Upload Payment Proof</h4>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-4">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileUpload}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-3 file:px-6 file:rounded-2xl file:border-0 file:text-sm file:font-bold file:bg-gradient-to-r file:from-green-500 file:to-emerald-600 file:text-white hover:file:from-green-600 hover:file:to-emerald-700 transition-all duration-300"
                  />
                  {offlineProof && (
                        <div className="flex items-center space-x-3 text-sm text-green-600 dark:text-green-400">
                          <FileText className="w-5 h-5" />
                          <span className="font-semibold">{offlineProof.name}</span>
                    </div>
                  )}
                </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                  Upload screenshot or PDF of your payment confirmation
                </p>
              </div>

              {/* Offline Payment Contact Details */}
                  <div className="mt-8 card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-green-200 dark:border-green-800 p-6">
                    <h4 className="text-lg font-bold text-green-800 dark:text-green-200 mb-6">Contact Details for Payment Verification</h4>
                
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                        <label className="block text-sm font-bold text-green-800 dark:text-green-200 mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={offlineFormData.name}
                      onChange={(e) => handleOfflineFormChange('name', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-green-300 dark:border-green-600 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                        <label className="block text-sm font-bold text-green-800 dark:text-green-200 mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={offlineFormData.email}
                      onChange={(e) => handleOfflineFormChange('email', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-green-300 dark:border-green-600 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div>
                        <label className="block text-sm font-bold text-green-800 dark:text-green-200 mb-3">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={offlineFormData.phone}
                      onChange={(e) => handleOfflineFormChange('phone', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-green-300 dark:border-green-600 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                      placeholder="Enter your phone number"
                      required
                    />
                  </div>

                  {/* WhatsApp */}
                  <div>
                        <label className="block text-sm font-bold text-green-800 dark:text-green-200 mb-3">
                      WhatsApp Number *
                    </label>
                    <input
                      type="tel"
                      value={offlineFormData.whatsapp}
                      onChange={(e) => handleOfflineFormChange('whatsapp', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-green-300 dark:border-green-600 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                      placeholder="Enter your WhatsApp number"
                      required
                    />
                  </div>
                </div>

                {/* Profile Save Message */}
                {user && (
                      <div className="mt-6 card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-blue-200 dark:border-blue-800 p-4">
                        <div className="flex items-center space-x-3">
                          <Check className="w-6 h-6 text-blue-600" />
                          <span className="text-blue-800 dark:text-blue-200 text-sm font-semibold">
                        These details are being added to your profile. You can change them later if needed.
                      </span>
                    </div>
                  </div>
                )}

                    <div className="mt-6 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                      <h5 className="text-lg font-bold text-green-800 dark:text-green-200 mb-4">Important Information</h5>
                      <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                        <li className="flex items-start space-x-2">
                          <span className="text-green-600 dark:text-green-400 font-bold">•</span>
                          <span>We will contact you to verify your payment</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-green-600 dark:text-green-400 font-bold">•</span>
                          <span>Please ensure the payment proof matches the order details</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-green-600 dark:text-green-400 font-bold">•</span>
                          <span>Your order will be processed after payment verification</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-green-600 dark:text-green-400 font-bold">•</span>
                          <span>You may be contacted on the provided phone or WhatsApp number</span>
                        </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* COD Payment Details */}
          {showCodDetails && (
                <div className="mb-8 card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-3xl shadow-xl border border-blue-200 dark:border-blue-800 p-8">
                  <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-100 mb-6 flex items-center gap-3">
                    <Truck className="w-8 h-8" />
                Cash on Delivery Contact Details
              </h3>
              
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                      <label className="block text-sm font-bold text-blue-800 dark:text-blue-200 mb-3">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={codFormData.name}
                    onChange={(e) => handleCodFormChange('name', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-blue-300 dark:border-blue-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                      <label className="block text-sm font-bold text-blue-800 dark:text-blue-200 mb-3">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={codFormData.email}
                    onChange={(e) => handleCodFormChange('email', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-blue-300 dark:border-blue-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                    placeholder="Enter your email address"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                      <label className="block text-sm font-bold text-blue-800 dark:text-blue-200 mb-3">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={codFormData.phone}
                    onChange={(e) => handleCodFormChange('phone', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-blue-300 dark:border-blue-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                    placeholder="Enter your phone number"
                    required
                  />
                </div>

                {/* WhatsApp */}
                <div>
                      <label className="block text-sm font-bold text-blue-800 dark:text-blue-200 mb-3">
                    WhatsApp Number *
                  </label>
                  <input
                    type="tel"
                    value={codFormData.whatsapp}
                    onChange={(e) => handleCodFormChange('whatsapp', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-blue-300 dark:border-blue-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                    placeholder="Enter your WhatsApp number"
                    required
                  />
                </div>
              </div>

              {/* Profile Save Message */}
              {user && (
                    <div className="mt-6 card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-blue-200 dark:border-blue-800 p-4">
                      <div className="flex items-center space-x-3">
                        <Check className="w-6 h-6 text-blue-600" />
                        <span className="text-blue-800 dark:text-blue-200 text-sm font-semibold">
                      These details are being added to your profile. You can change them later if needed.
                    </span>
                  </div>
                </div>
              )}

                  <div className="mt-6 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <h4 className="text-lg font-bold text-blue-800 dark:text-blue-200 mb-4">Important Information</h4>
                    <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                      <li className="flex items-start space-x-2">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                        <span>You will be contacted on the provided phone number for order confirmation</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                        <span>Payment will be collected at the time of delivery</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                        <span>Please ensure someone is available at the delivery address</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                        <span>Valid ID proof may be required for delivery</span>
                      </li>
                </ul>
              </div>
            </div>
          )}

          {/* Save Details Checkbox */}
          <div className="mb-8">
                <label className="flex items-center space-x-3 p-4 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 hover:scale-105 transition-all duration-300 cursor-pointer">
              <input
                type="checkbox"
                checked={saveDetails}
                onChange={(e) => setSaveDetails(e.target.checked)}
                    className="w-5 h-5 rounded border-2 border-blue-300 text-blue-600 focus:ring-blue-500 focus:ring-2"
              />
                  <span className="text-gray-700 dark:text-gray-300 font-semibold">
                Save my payment details for future purchases
              </span>
            </label>
          </div>

          {/* Order Summary */}
              <div className="card-3d bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 backdrop-blur-md rounded-3xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Order Summary
            </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                    <span className="text-gray-600 dark:text-gray-400 font-semibold">Items ({items.length}):</span>
                    <span className="text-gray-900 dark:text-white font-semibold">
                  {items.map(item => item.name).join(', ')}
                </span>
              </div>
              
              {/* Payment Option Display */}
              {(() => {
                // Check if this is an installment payment
                const hasPaymentItems = items.some(item => item.type === 'payment');
                const paymentItem = hasPaymentItems ? items.find(item => item.type === 'payment') : null;
                
                if (hasPaymentItems && paymentItem?.paymentData) {
                  // Display installment payment information
                  const currentInstallment = parseInt(paymentItem.paymentData.current_installment) || 1;
                  const totalInstallments = parseInt(paymentItem.paymentData.total_installments) || 1;
                  const paymentType = paymentItem.paymentData.type === 'emi_payment' ? 'EMI' : 'Split Payment';
                  
                  return (
                        <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">Payment Type:</span>
                          <span className="font-bold text-orange-600 dark:text-orange-400">
                        {paymentType} - Installment {currentInstallment} of {totalInstallments}
                      </span>
                    </div>
                  );
                } else if (checkoutData?.selectedPaymentOption) {
                  // Display regular payment option
                  return (
                        <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">Payment Type:</span>
                          <span className={`font-bold ${
                        checkoutData.selectedPaymentOption === 'advance' 
                          ? 'text-blue-600 dark:text-blue-400' 
                          : checkoutData.selectedPaymentOption === 'emi'
                          ? 'text-purple-600 dark:text-purple-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {getPaymentOptionText()}
                      </span>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Total Order Amount */}
                  <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-600">
                    <span className="text-gray-600 dark:text-gray-400 font-semibold">Total Order Amount:</span>
                    <span className="text-gray-900 dark:text-white font-bold text-lg">{formatPrice(getTotal())}</span>
              </div>

              {/* Amount to Pay */}
                  <div className="flex justify-between items-center py-4 border-t-2 border-blue-200 dark:border-blue-700">
                    <span className="text-gray-900 dark:text-white font-bold text-xl">
                  {checkoutData?.selectedPaymentOption === 'advance' ? 'Amount to Pay Now:' : 
                   checkoutData?.selectedPaymentOption === 'emi' ? 'Advance Amount:' : 'Total Amount:'}
                </span>
                    <span className="text-gray-900 dark:text-white font-bold text-2xl">{formatPrice(getPaymentAmount())}</span>
              </div>

              {/* Payment Option Details */}
              {checkoutData?.selectedPaymentOption === 'advance' && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 text-center mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-700">
                  Remaining {formatPrice(getTotal() - getPaymentAmount())} will be charged after demo and project completion
                </div>
              )}
              
              {checkoutData?.selectedPaymentOption === 'emi' && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 text-center mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-2xl border border-purple-200 dark:border-purple-700">
                  Pay {paymentOptionsConfig?.emi_advance_percentage || 50}% advance now, remaining amount in EMI for {paymentOptionsConfig?.emi_tenure_months?.split(',')[0] || '3'} months at {paymentOptionsConfig?.emi_interest_rate || 15}% p.a.
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:space-x-6">
            <button
              onClick={handleBackToCheckout}
                  className="flex-1 px-8 py-4 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 flex items-center justify-center space-x-3 font-bold hover:scale-105 shadow-lg"
            >
                  <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <button
              onClick={handleConfirmPayment}
              disabled={!selectedPayment || loading || (selectedPayment === 'offline' && (!offlineProof || !offlineFormData.name || !offlineFormData.email || !offlineFormData.phone || !offlineFormData.whatsapp)) || (selectedPayment === 'cod' && (!codFormData.name || !codFormData.email || !codFormData.phone || !codFormData.whatsapp))}
                  className="flex-1 px-8 py-4 card-3d bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold rounded-2xl transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed flex items-center justify-center space-x-3 shadow-glow"
            >
              {loading ? (
                <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <span>Confirm Payment</span>
                      <CheckCircle className="w-5 h-5" />
                </>
              )}
            </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment; 